import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  MenuItem,
  ToggleButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { DedicatedDeliveryAgent } from "../../../../../app/api/dedicatedDelivery/DedicatedDeliveryAgent";
import {
  DdStoreLateCancels,
  DdStorePerformanceResponse,
} from "../../../../../app/models/responseModels/DdStorePerformanceResponse";
import { CustomTooltip } from "../../../../../components/common/tooltip/CustomToolTip";
import { format, parseISO } from "date-fns";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../app/features/store/store";
import { getDefaultMRTOptions } from "../../../DefaultMrtOptions";
import { FdpResponse } from "../../../../../app/models/responseModels/FdpResponse";

interface StoreLateCancelProps {
  metaData: DdStorePerformanceResponse;
}
const StoreLateCancel: React.FC<StoreLateCancelProps> = ({ metaData }) => {
  const defaultMRTOptions_lateCancels =
    getDefaultMRTOptions<DdStoreLateCancels>();

    const [invoiceLateCancels, setInvoiceLateCancels] = useState<any>(
      [...metaData.ddLateCancels].sort((a, b) => 
        new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
      )
    );
  const { authResponse } = useSelector((state: RootState) => state.login);

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});

  const lateCancelColumns = useMemo<MRT_ColumnDef<DdStoreLateCancels>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
      },
      {
        accessorKey: "operationDate",
        header: "Date",
        Cell: ({ cell, row }) =>
          row.original.operationDate &&
          format(parseISO(row.original.operationDate), "MM/dd/yyyy"),
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          error: !!validationErrors?.operationDate,
          helperText: validationErrors?.operationDate,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              operationDate: undefined,
            }),
        },
      },
      {
        accessorKey: "noOfRoutes",
        header: "Route Count",
        muiEditTextFieldProps: {
          type: "number",
          required: true,
          error: !!validationErrors?.noOfRoutes,
          helperText: validationErrors?.noOfRoutes,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              noOfRoutes: undefined,
            }),
        },
      },
      {
        accessorKey: "fdpId",
        header: "FDP",
        Cell: ({ row }) => row.original.fdpName,
        editVariant: "select",
        muiEditTextFieldProps: ({ row }) => ({
          select: true,
          required: true,
          children: metaData.fdps.map((fdp: FdpResponse) => (
            <MenuItem key={fdp.id} value={fdp.id}>
              {fdp.companyName}
            </MenuItem>
          )),
          error: !!validationErrors?.fdpId,
          helperText: validationErrors?.fdpId,
          onFocus: () => {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              fdpId: undefined,
            }));
          },
        }),
      },
      {
        accessorKey: "isEnabled",
        header: "Active",
        Cell: ({ cell, row }) => (row.original.isEnabled ? "Yes" : "No"),
        editSelectOptions: [
          { label: "Yes", text: "Yes", value: true },
          { label: "No", text: "No", value: false },
        ],
        editVariant: "select",
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.isEnabled,
          helperText: validationErrors?.isEnabled,
        },
      },
      {
        accessorKey: "isInvoiced",
        header: "Is Invoiced",
        enableEditing: false,
        Cell: ({ cell, row }) => (row.original.isInvoiced ? "TRUE" : "FALSE"),
      },
      {
        accessorKey: "createdOn",
        header: "Created On",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.createdOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "updatedOn",
        header: "Updated On",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.updatedOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        enableEditing: false,
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        enableEditing: false,
      },
    ],
    [validationErrors]
  );

  const validateLateCancellation = (values: any) => {
    return {
      date: !values.operationDate ? "Date is Required" : "",
      noOfRoutes: !values.noOfRoutes 
        ? "Routes count is Required" 
        : parseFloat(values.noOfRoutes) < 0 
          ? "Routes count cannot be negative" 
          : "",
      fdpId: !values.fdpId ? "FDP is Required" : "",
    };
  };

  const handleSaveLateCancel: MRT_TableOptions<DdStoreLateCancels>["onEditingRowSave"] =
    async ({ values, table, row }) => {
      console.log(metaData);
      console.log(values);
      const newValidationErrors = validateLateCancellation(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      try {
        updateLateCancelAsync(values, row.original.id);
      } catch (e) {
        toast.error(
          "Server error occurred while submiting Late Cancel data. Please try again later."
        );
      } finally {
        table.setEditingRow(null);
      }
    };

  const updateLateCancelAsync = async (values: any, rowId: any) => {
    const response = await DedicatedDeliveryAgent.updateLateCancelToStore({
      id: rowId,
      ddStoreId: metaData.id,
      fdpId: values.fdpId,
      operationDate: values.operationDate,
      noOfRoutes: values.noOfRoutes,
      isEnabled: values.isEnabled,
      isInvoiced: false,
      updatedBy: authResponse?.userSettings?.userName ?? "",
    });
    if (response && response.id) {
      toast.success("A Late Cancel has been modified.");
      console.log(response);
      setInvoiceLateCancels((prev: any) => {
        const updatedClaims = prev.map((lateCancel: any) =>
          lateCancel.id === response.id ? response : lateCancel
        );
        // Sort by creation date after updating
        return updatedClaims.sort((a: any, b: any) => 
          new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
        );
      });
    }
  };

  const handleToggleLateCancel = async (row: MRT_Row<DdStoreLateCancels>) => {
    console.log(row.original);
    await updateLateCancelAsync(
      {
        id: row.id,
        ddStoreId: row.original.id,
        fdpId: row.original.fdpId,
        operationDate: row.original.operationDate,
        noOfRoutes: row.original.noOfRoutes,
        isEnabled: !row.original.isEnabled,
        isInvoiced: row.original.isInvoiced,
        updatedBy: authResponse?.userSettings?.userName ?? "",
      },
      row.original.id
    );
  };

  const handleCreateLateCancel: MRT_TableOptions<DdStoreLateCancels>["onCreatingRowSave"] =
    async ({ values, table }) => {
      console.log(values, metaData.id);
      const newValidationErrors = validateLateCancellation(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      try {
        setValidationErrors({});
        const response = await DedicatedDeliveryAgent.addLateCancelToStore({
          ddStoreId: metaData.id,
          fdpId: values.fdpId,
          operationDate: values.operationDate,
          noOfRoutes: values.noOfRoutes,
          isEnabled: values.isEnabled,
          isInvoiced: false,
          createdBy: authResponse?.userSettings?.userName ?? "",
          updatedBy: authResponse?.userSettings?.userName ?? "",
        });
        if (response && response.id) {
          toast.success("A Late Cancel has been added.");
          setInvoiceLateCancels((prev: any) => {
            const updatedLateCancels = [...prev, response];
            // Sort by creation date after adding new entry
            return updatedLateCancels.sort((a, b) => 
              new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
            );
          });
        }
      } catch (e) {
        toast.error(
          "Server error occurred while submiting Late Cancel data. Please try again later."
        );
      } finally {
        table.setCreatingRow(null);
      }
    };

  // preprare MRT V2 grid
  const lateCancelTable = useMaterialReactTable({
    ...defaultMRTOptions_lateCancels,
    columns: lateCancelColumns,
    data: invoiceLateCancels,
    getRowId: (row) => row.id.toString(),
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveLateCancel,
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateLateCancel,
    initialState: {
      ...defaultMRTOptions_lateCancels.initialState,
      columnVisibility: {
        id: false,
      },
      sorting: [{ desc: true, id: "createdOn" }],
    },
    state: {
      sorting: [{ desc: true, id: "createdOn" }],
    },
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {invoiceLateCancels.length}
        </Typography>
      );
    },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => {
              setValidationErrors({});
              table.setEditingRow(row);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <ToggleButton
          value="Enable/Disable"
          selected={row.original.isEnabled}
          onChange={() => handleToggleLateCancel(row)}
        >
          <BlockIcon />
        </ToggleButton>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <CustomTooltip
        arrow
        title="Add New"
        onClick={() => {
          table.setCreatingRow(true);
        }}
      >
        <IconButton type="button">
          <AddIcon sx={{ color: "primary.main" }} />
        </IconButton>
      </CustomTooltip>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      ...defaultMRTOptions_lateCancels.muiTableBodyRowProps,
      sx: {
        bgcolor: row.original.isEnabled === false ? "grey" : undefined,
      },
    }),
  });
  return <MaterialReactTable table={lateCancelTable} />;
};

export default StoreLateCancel;
